import {
  type CustomerQuote,
  getMarketDataSnapshotMid,
  logger,
  type MarketDataSnapshot,
  validateMidPriceDiff,
} from '@talos/kyoko';
import { formatInTimeZone } from 'date-fns-tz';
import { compact } from 'lodash-es';
import type { FixingState } from './fixingSlice';

export interface FixingPriceWarning {
  symbol: string;
  fixing: string;
  mid: string;
}

/**
 * Called upon the user clicking Apply Fixing. In this function we compare the user's applied fixings to our
 * MDS records to defend against fat fingers, or for example if the user accidentally entered the ETH-USD fixing
 * into the BTC-USD input field.
 */
export function getMidPriceFixingWarnings(
  state: FixingState,
  mdsMap: Map<string, MarketDataSnapshot>
): FixingPriceWarning[] {
  const warnings = state.fixings.map(fixing => {
    if (!fixing.field.value || !fixing.symbol) {
      // If there is no field.value, or there is no symbol for the fixing, warnings are not applicable
      return undefined;
    }

    const mid = getMarketDataSnapshotMid(mdsMap.get(fixing.symbol));
    if (!mid) {
      // No mid price, return undefined ("no warning, all good")
      return undefined;
    }

    const valid = validateMidPriceDiff(mid, fixing.field.value, 0.05); // 5%
    if (valid) {
      return undefined;
    }

    return { symbol: fixing.symbol, fixing: fixing.field.value, mid };
  });

  return compact(warnings);
}

export function getAllFixingIndicesFromCustomerQuotes(customerQuotes: CustomerQuote[]): Set<string> {
  const allFixingIndices = new Set<string>();
  customerQuotes.forEach(cq => cq.fixingIndices?.forEach(fixingIndex => allFixingIndices.add(fixingIndex)));
  return allFixingIndices;
}

/**
 * Returns the fixing mark calendar date in the index timezone when available.
 * Falls back to raw date-only extraction from the timestamp if timezone is unavailable.
 */
export function getIndexMarkDateOnly(
  nominalTime: string | undefined,
  nominalIndexTimeZone: string | undefined
): string | undefined {
  if (!nominalTime || !nominalIndexTimeZone) {
    return undefined;
  }

  try {
    return formatInTimeZone(nominalTime, nominalIndexTimeZone, 'yyyy-MM-dd');
  } catch (e) {
    logger.error(new Error('Failed to format index mark date in nominal index timezone'), {
      extra: {
        nominalTime,
        nominalIndexTimeZone,
        error: e instanceof Error ? e.message : String(e),
      },
    });
    return undefined;
  }
}
